import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";

const distribuirEnLineas = (texto, cantidadLineas = 4) => {
  const palabras = String(texto || "").trim().split(/\s+/).filter(Boolean);

  if (!palabras.length) {
    return [""];
  }

  const lineas = [];
  let indice = 0;

  for (let numeroLinea = 0; numeroLinea < cantidadLineas; numeroLinea += 1) {
    const lineasRestantes = cantidadLineas - numeroLinea;
    const palabrasRestantes = palabras.slice(indice);

    if (lineasRestantes === 1) {
      lineas.push(palabrasRestantes.join(" "));
      break;
    }

    const caracteresRestantes = palabrasRestantes.join(" ").length;
    const objetivo = caracteresRestantes / lineasRestantes;
    const palabrasLinea = [];

    while (indice < palabras.length) {
      const palabrasQueQuedan = palabras.length - indice;
      if (palabrasQueQuedan <= lineasRestantes - 1) {
        break;
      }

      const candidata = [...palabrasLinea, palabras[indice]].join(" ");
      if (
        palabrasLinea.length > 0 &&
        Math.abs(palabrasLinea.join(" ").length - objetivo) <=
          Math.abs(candidata.length - objetivo)
      ) {
        break;
      }

      palabrasLinea.push(palabras[indice]);
      indice += 1;
    }

    lineas.push(palabrasLinea.join(" "));
  }

  return lineas;
};

/*
 * La situación se ajusta al ancho disponible y se recorta a 4 líneas;
 * si el texto es más largo se puede desplegar completo.
 */
const SituacionSignificativa = ({ texto }) => {
  const ref = useRef(null);
  const [expandida, setExpandida] = useState(false);
  const [recortada, setRecortada] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || expandida) return undefined;

    const medir = () => setRecortada(el.scrollHeight > el.clientHeight + 1);
    medir();

    const observer = new ResizeObserver(medir);
    observer.observe(el);
    return () => observer.disconnect();
  }, [texto, expandida]);

  return (
    <>
      <span
        ref={ref}
        className={`step4-meta-context-value${expandida ? "" : " step4-meta-context-clamp"}`}
      >
        {texto}
      </span>
      {(recortada || expandida) && (
        <button
          type="button"
          className="step4-meta-context-toggle"
          onClick={() => setExpandida((valor) => !valor)}
        >
          {expandida ? "Ver menos" : "Ver más"}
        </button>
      )}
    </>
  );
};

const SessionSequenceHeader = ({ firstRow, hayCambios, loadingGeneral, onOpenRegenerateAll }) => {
  const situacionSignificativa =
    firstRow?.situacion ||
    firstRow?.situacionSignificativa ||
    "No se registró una situación significativa para esta unidad.";

  const lineasUnidad = distribuirEnLineas(firstRow?.unidadNombre || "-", 2);

  return (
  <>
    <Box
      sx={{
        mb: 1,
        paddingRight: 2,
        pt: 1,
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: "100%" }}>
        <Typography variant="h4" className="step4-title">
          Secuencia SAC
        </Typography>
        <Box sx={{ borderLeft: "1px solid #9e9e9e", height: "30px", marginLeft: 2, marginRight: 2 }} />
        <Typography variant="body2" sx={{ color: "#9e9e9e", maxWidth: 420 }}>
          Revisión y ajuste de títulos y descripciones
        </Typography>
      </Box>
    </Box>

    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        width: "100%",
        pb: 1,
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Box className="step4-meta-compact">
        <Box className="step4-meta-summary">
          <Box className="step4-meta-line">
            <div>
              <span className="step4-meta-label">Nivel</span>
              <span className="step4-meta-value">{firstRow?.nivelNombre || "-"}</span>
            </div>
            <div>
              <span className="step4-meta-label">Grado</span>
              <span className="step4-meta-value">{firstRow?.gradoNombre || "-"}</span>
            </div>
          </Box>

          <Box className="step4-meta-line">
            <div>
              <span className="step4-meta-label">Área Temática</span>
              <span className="step4-meta-value">{firstRow?.areaNombre || "-"}</span>
            </div>
            <div>
              <span className="step4-meta-label">Unidad</span>
              <span className="step4-meta-value step4-meta-unit-value">
                {lineasUnidad.map((linea, index) => (
                  <span key={`${index}-${linea}`} className="step4-meta-unit-line">
                    {linea}
                  </span>
                ))}
              </span>
            </div>
          </Box>
        </Box>

        <Box className="step4-meta-context">
          <span className="step4-meta-label">Situación significativa</span>
          <SituacionSignificativa texto={situacionSignificativa} />
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0 }}>
        <Button
          variant="contained"
          startIcon={<AutorenewIcon />}
          onClick={onOpenRegenerateAll}
          disabled={loadingGeneral || hayCambios}
          className="step4-btn step4-btn-regenerate"
        >
          Regenerar temas
        </Button>

        {hayCambios && (
          <Typography variant="caption" sx={{ color: "#9e9e9e", mt: 0.5, textAlign: "right", marginLeft: 2 }}>
            Guarda los cambios antes de regenerar todos los temas.
          </Typography>
        )}
      </Box>
    </Box>
  </>
  );
};

export default SessionSequenceHeader;
